import React from 'react';
import { motion, Variants } from 'framer-motion';
import { Home, Key, Maximize, RefreshCw } from 'lucide-react';
import { LivingInAreaData } from '../../types/livingInArea';

interface HousingSectionProps {
  data: LivingInAreaData['housingReality'];
}

// --- Animation Variants ---
const statVariants: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
};

export const HousingSection: React.FC<HousingSectionProps> = ({ data }) => {
  return (
    <section className="max-w-7xl mx-auto px-4 py-12 space-y-16">
      {/* Section Header - Improved Contrast */}
      <div className="text-center space-y-4">
        <h2 className="text-5xl font-black text-slate-900 tracking-tight">
          Housing <span className="text-indigo-600">Reality</span>
        </h2>
        <p className="text-xl font-bold text-slate-600 max-w-2xl mx-auto">
          What to expect from the property market in this area.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-5">
        {/* Main Housing Card - High Contrast Dark Mode */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5 }}
          className="relative lg:col-span-3 overflow-hidden rounded-[3rem] bg-slate-900 
                     p-10 md:p-14 text-white shadow-2xl
                     bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950"
        >
          {/* Background decoration */}
          <div className="absolute -bottom-10 -right-10 opacity-10 text-indigo-400">
            <Home size={280} strokeWidth={1} />
          </div>

          <div className="relative z-10 space-y-8">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Home size={24} className="text-indigo-300" />
              </div>
              <span className="text-xs font-black uppercase tracking-[0.2em] text-indigo-300">
                Dominant Property Type
              </span>
            </div>

            <h3 className="text-4xl md:text-6xl font-black tracking-tighter text-white">
              {data.dominantType}
            </h3>

            <p className="text-xl md:text-2xl font-medium leading-relaxed text-slate-200">
              {data.insight}
            </p>

            {/* Secondary Types with enhanced tag visibility */}
            {data.secondaryTypes && data.secondaryTypes.length > 0 && (
              <div className="pt-8 border-t border-white/10">
                <p className="mb-4 text-xs font-black uppercase tracking-widest text-slate-400">
                  Also Commonly Available
                </p>
                <div className="flex flex-wrap gap-3">
                  {data.secondaryTypes.map((type, i) => (
                    <span
                      key={i}
                      className="rounded-xl border-2 border-white/20 bg-white/5 px-5 py-2 text-sm font-bold shadow-sm backdrop-blur-sm"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Stats Column - High Contrast Light Cards */}
        <div className="lg:col-span-2 space-y-6">
          <HousingStatCard
            icon={<Key />}
            label="Ownership Feel"
            value={data.ownershipFeel}
            color="blue"
            delay={0.2}
          />

          <HousingStatCard
            icon={<Maximize />}
            label="Space Expectation"
            value={data.spaceExpectation}
            color="teal"
            delay={0.3}
          />

          {data.turnoverRate && (
            <HousingStatCard
              icon={<RefreshCw />}
              label="Turnover Rate"
              value={data.turnoverRate}
              color="purple"
              delay={0.4}
            />
          )}
        </div>
      </div>
    </section>
  );
};

// Sub-component for individual stat cards to ensure consistency
const HousingStatCard = ({ icon, label, value, color, delay }: any) => {
  const colorMap: any = {
    blue: 'bg-blue-600 text-blue-100',
    teal: 'bg-teal-600 text-teal-100',
    purple: 'bg-purple-600 text-purple-100',
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.8 }}
      variants={statVariants}
      transition={{ delay }}
      className="rounded-[2.5rem] border-2 border-slate-200 bg-white p-8 shadow-xl transition-all"
    >
      <div className="mb-4 flex items-center gap-4">
        <div className={`rounded-2xl p-3 shadow-lg ${colorMap[color]}`}>
          {React.cloneElement(icon, { size: 24, strokeWidth: 2.5 })}
        </div>
        <span className="text-xs font-black uppercase tracking-widest text-slate-500">{label}</span>
      </div>
      <p className="text-2xl font-black text-slate-900 leading-tight">{value}</p>
    </motion.div>
  );
};
